// Ian - 2026-08-13 - New PDP (Aug 2026 trial). Quantity stepper + add to basket on the
// below-the-fold product cards.
//
// Why this is not the theme's own <product-form>: product-form.js reads
// document.querySelector('.complimentary-products') on every submit, and that lookup is not
// scoped to the form being submitted. On this template main-product renders a complementary
// products block, so adding a card from further down the page would silently add whatever
// quantities were sitting in that block too. This element does one product and nothing else.
//
// Everything else deliberately matches product-form.js rather than inventing a parallel
// contract: the same /cart/add.js call with the same section-rendering payload, the same
// theme.settings.afterAtc branching, the same cart-drawer.renderContents() call, and the
// event contract documented in custom.js. That contract is load-bearing beyond analytics:
// snippets/cart-items.liquid listens for on:line-item:change, matches the line by
// variant_id AND newQuantity, and writes the "Length: N metres" property onto the order
// line. A wrong newQuantity means no cut length on the order.

if (!customElements.get('ian-pdp-add')) {
  class IanPdpAdd extends HTMLElement {
    connectedCallback() {
      // Guarded: without this a DOM move re-binds the click handler and one press of the
      // plus button steps twice.
      if (this.initialised) return;

      this.input = this.querySelector('.ian-pdp__qty-input');
      this.addBtn = this.querySelector('[data-ian-pdp-add]');
      this.status = this.querySelector('.ian-pdp__card-status');
      this.variantId = this.dataset.variantId;
      if (!this.input || !this.addBtn || !this.variantId) return;

      // Fabric is sold by the half metre, so one Shopify unit is 0.5m. The conversion lives
      // in one place (the data attribute, written once in the card snippet) rather than as a
      // hardcoded /2 scattered through the JS.
      this.unitsPerMetre = parseFloat(this.dataset.unitsPerMetre) || 1;
      this.perMetre = this.dataset.perMetre === 'true';

      this.unitPrice = parseInt(this.dataset.unitPrice, 10);
      this.ctaLabel = this.dataset.ctaLabel || 'Add to basket';

      this.addEventListener('click', this.handleClick.bind(this));
      this.initialised = true;
      this.syncStepButtons();
      this.renderTotal();
    }

    // The CTA carries the running total, which is the whole reason the mock's buy box gives
    // that line its width. Formatted through Intl against the storefront's active currency
    // rather than a hardcoded symbol, so it follows Shopify Markets.
    renderTotal() {
      const target = this.querySelector('[data-ian-pdp-cta-text]');
      if (!target || !Number.isFinite(this.unitPrice)) return;

      // Not in the patterns card. That card is narrow and already shows the price directly
      // above the button, so the total earns nothing there and "· £19.80" broke out of the
      // button. The two card contexts diverge here deliberately rather than by accident:
      // the total stays on the wider "Also worth a look" cards, where it does work.
      if (this.closest('.ian-pdp__cards--stacked')) return;
      const total = this.unitPrice * this.quantityInUnits;
      let money;
      try {
        money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP'
        }).format(total / 100);
      } catch (error) {
        return; // Leave the plain label rather than risk a wrong-looking price.
      }
      target.textContent = `${this.ctaLabel} · ${money}`;
    }

    get errorText() {
      return (window.theme && window.theme.strings && window.theme.strings.cartError)
        || 'Sorry, that could not be added to your basket.';
    }

    handleClick(evt) {
      const stepBtn = evt.target.closest('[data-ian-pdp-qty]');
      if (stepBtn) {
        evt.preventDefault();
        if (stepBtn.dataset.ianPdpQty === 'up') {
          this.input.stepUp();
        } else {
          this.input.stepDown();
        }
        this.syncStepButtons();
        this.renderTotal();
        return;
      }

      if (evt.target.closest('[data-ian-pdp-add]')) {
        evt.preventDefault();
        this.add();
      }
    }

    // The theme disables its own plus button at the inventory ceiling. Without this the
    // button is a silent no-op at the limit and the customer keeps pressing it.
    syncStepButtons() {
      const value = parseFloat(this.input.value);
      const min = parseFloat(this.input.min);
      const max = parseFloat(this.input.max);
      const minus = this.querySelector('[data-ian-pdp-qty="down"]');
      const plus = this.querySelector('[data-ian-pdp-qty="up"]');
      if (minus) minus.disabled = !Number.isNaN(min) && value <= min;
      if (plus) plus.disabled = !Number.isNaN(max) && value >= max;
    }

    get quantityInUnits() {
      const value = parseFloat(this.input.value) || 0;
      const units = this.perMetre ? Math.round(value * this.unitsPerMetre) : Math.round(value);
      return Math.max(units, 1);
    }

    // Throws rather than returning null. Emitting on:cart:add with cart: null is worse than
    // emitting an error, because listeners doing detail.cart.items throw inside third-party
    // code. product-form.js throws here too.
    static async fetchCart() {
      const route = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      const response = await fetch(`${route}.js`);
      if (!response.ok) throw new Error(response.status);
      return response.json();
    }

    async add() {
      if (this.addBtn.getAttribute('aria-disabled') === 'true') return;

      // Set synchronously, before the first await, so a double click cannot get through.
      this.addBtn.setAttribute('aria-disabled', 'true');
      this.addBtn.classList.add('is-loading');
      this.setStatus('');

      const afterAtc = (window.theme && window.theme.settings && window.theme.settings.afterAtc) || 'drawer';
      const cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      const cartDrawer = document.querySelector('cart-drawer');

      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', this.quantityInUnits);

      // The cut length on the order line. The buy button gets this from a hidden
      // properties[Length] input and from the fetch interceptor in main-product.liquid,
      // but that interceptor only looks at .js-complimentary-product containers, so it
      // never sees these cards and a card add produced a line with no Length at all.
      // Sent with the add rather than patched on afterwards: one request, no race.
      //
      // The string format matches main-product.liquid exactly, including its "1 metres"
      // pluralisation. That is deliberate and load-bearing, not a copied typo: Shopify
      // treats lines whose properties differ as separate lines, so "1 metre" here would
      // split the basket into two lines instead of merging with the buy button's.
      if (this.perMetre) {
        formData.append('properties[Length]', `${this.input.value} metres`);
      }

      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        const drawerSection = cartDrawer.closest('.shopify-section');
        if (drawerSection) sections += `,${drawerSection.id.replace('shopify-section-', '')}`;
      }
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      try {
        const oldCart = await IanPdpAdd.fetchCart();

        const addRoute = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
        const response = await fetch(addRoute, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        // Parsed defensively: a bot challenge or an HTML error page makes .json() throw, and
        // the parser's message ("Unexpected token '<'...") must never reach a customer.
        const data = await response.json().catch(() => null);

        if (!response.ok || !data) {
          throw new Error(this.messageFor(data));
        }

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections && data.sections['cart-icon-bubble']) {
          bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (afterAtc === 'page') {
          setTimeout(() => { window.location.href = cartRoute; }, 300);
        } else if (cartDrawer) {
          // Only open the drawer when the merchant has asked for it, exactly as
          // product-form.js does. Otherwise refresh its contents in place.
          await cartDrawer.renderContents(data, afterAtc === 'drawer');
        } else if (window.location.pathname === cartRoute) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) cartItems.refresh();
        }

        const newCart = await IanPdpAdd.fetchCart();
        const existingLine = oldCart.items.find((item) => item.variant_id === data.variant_id);
        const newLine = newCart.items.find((item) => item.variant_id === data.variant_id);

        if (existingLine) {
          // Read off the fresh cart, never derived. The add response's quantity is already
          // the post-add line total, so existingLine.quantity + data.quantity double counts,
          // which breaks the Length property lookup in cart-items.liquid.
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCart,
              variantId: data.variant_id,
              oldQuantity: existingLine.quantity,
              newQuantity: newLine ? newLine.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { cart: newCart, variantId: data.variant_id }
          }));
        }

        // The theme's tick animation only has content while is-loading is also present, so
        // both classes stay on for the length of it.
        this.addBtn.classList.add('is-success');
        setTimeout(() => {
          this.addBtn.classList.remove('is-loading');
          this.addBtn.classList.remove('is-success');
        }, 1400);
      } catch (error) {
        this.addBtn.classList.remove('is-loading');
        const message = (error && error.message) ? error.message : this.errorText;
        this.setStatus(message);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: message }
        }));
        if (cartDrawer) cartDrawer.refresh(true);
      } finally {
        this.addBtn.removeAttribute('aria-disabled');
        this.syncStepButtons();
      }
    }

    // Shopify localises its own 422 bodies, so those are worth showing, with one exception:
    // its inventory-ceiling message counts in UNITS ("You can only add 3 of this item"),
    // which reads as metres beside a control labelled Metre and sends the customer round in
    // circles. Per-metre products get the generic line instead.
    messageFor(data) {
      if (!data) return this.errorText;
      if (this.perMetre) return this.errorText;
      if (data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors).flat().join(', ');
      }
      if (typeof data.description === 'string') return data.description;
      if (typeof data.message === 'string') return data.message;
      return this.errorText;
    }

    // The status element stays in the accessibility tree at all times. Toggling `hidden` in
    // the same tick as the text change means NVDA and VoiceOver never announce it, so
    // visibility is handled by :empty in the stylesheet instead.
    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
    }
  }

  customElements.define('ian-pdp-add', IanPdpAdd);
}
